// Schema and mappers between UI project shape and Salesforce Project__c fields

// Minimal UI -> SF field map for create/update (only fields we control)
// Note: We intentionally DO NOT map UI 'category' to SF 'Job_Category__c' due to picklist mismatch.
// Note: Supports both 'info.*' (internal schema) and 'info.*' (component usage) for backward compatibility
export const uiToSfFieldMap = {
  // Core metadata - ORDER MATTERS: info.* first for toSalesforceProject, then info.* wins for fromSalesforceProject
  "info.name": "Name",
  "info.shortName": "Short_Project_Name__c",
  "info.shortDescription": "Project_Short_Description__c",
  "info.longDescription": "Project_Long_Description__c",
  "info.workdayProjectId": "Workday_Project_ID__c",
  "info.programName": "Program_Name__c",
  "info.hireStartDate": "Hire_Start_Date__c",
  "info.category": "Job_Category__c",
  "info.predictedCloseDate": "Predicted_Close_Date__c",
  "info.projectStatus": "Project_Status__c",
  "info.projectType": "Project_Type__c",
  "info.projectPriority": "Project_Priority__c",
  "info.projectPaymentMethod": "Project_Payment_Method__c",
  "info.appenPartner": "Appen_Partner__c",
  "info.account": "Account__c", // expects 18-char Account Id
  "info.accountName": "Account__r.Name",
  "info.projectManagerId": "Project_Manager__c",
  "info.projectManagerName": "Project_Manager__r.Name",

  // Objective-related fields stored at Project level
  "objectives.contributorFacingName": "Contributor_Project_Name__c",
  "objectives.objectiveName": "Project_Objective_Name__c",
  "objectives.appendToObjectiveName": "Append_to_Objective_Name__c",
  "objectives.project": "Project__c",
  "objectives.dateStart": "Date_Start__c",
  "objectives.dateEnd": "Date_End__c",
  "objectives.selectionCriteria": "Selection_Criteria__c",
  "objectives.projectObjectiveDescription": "Project_Objective_Description__c",
  "objectives.totalTargetProductivityHours":
    "Total_Target_Productivity_Hours__c",
  "objectives.weeklyTargetProductionHours": "Weekly_Target_Production_Hours__c",
  "objectives.weeklyContributorProductionHours":
    "Weekly_Contributor_Production_Hours__c",
  "objectives.targetContributors": "Target_Contributors__c",
  "objectives.projectObjectiveIdForReports":
    "Project_Objective_ID_for_Reports__c",

  // Requirements fields
  "objectives.country": "Country__c",
  "objectives.language": "Language__c",
  "objectives.workType": "Work_Type__c",
  "objectives.dialect": "Dialect__c",
  "objectives.degreeRequirement": "Degree_Requirement__c",
  "objectives.languageSkillLevel": "Language_Skill_Level__c",
  "objectives.additionalManualMatchRequired":
    "Additional_Manual_Match_Required__c",
  "objectives.fluencyType": "Fluency_Type__c",
  "objectives.includeInCountryList": "Include_in_Country_List__c",

  // Job Posting fields
  "objectives.jobPostAction": "Job_Post_Action__c",
  "objectives.jobPostStatus": "Job_Post_Status__c",
  "objectives.leverJobPostingDistribution": "Lever_Job_Posting_Distribution__c",
  "objectives.jobDescriptionTemplate": "Job_Description_Template__c",
  "objectives.jobPostingTitle": "Job_Posting_Title__c",
  "objectives.jobDescription": "Job_Description__c",
  "objectives.leverTeam": "Lever_Team__c",
  "objectives.leverRequisitionId": "Lever_Requisition_ID__c",
  "objectives.locationType": "Location_Type__c",
  "objectives.leverJobPostingDate": "Lever_Job_Posting_Date__c",
  "objectives.leverDepartment": "Lever_Department__c",
  "objectives.leverJobPostingId": "Lever_Job_Posting_ID__c",
  "objectives.leverLocation": "Lever_Location__c",
  "objectives.leverWorkType": "Lever_Work_Type__c",

  // Productivity & Quality fields
  "objectives.productivityTargetType": "Productivity_Target_Type__c",
  "objectives.targetProductionHours": "Target_Production_Hours__c",
  "objectives.mainQualityMetric": "Main_Quality_Metric__c",
  "objectives.contributorQualityFlag": "Contributor_Quality_Flag__c",
  "objectives.contributorProductivityFlag": "Contributor_Productivity_Flag__c",
  "objectives.contributorOverproductivityFlag":
    "Contributor_Overproductivity_Flag__c",

  // Funnel Configuration fields
  "objectives.funnelAPercentage": "Funnel_A_Percentage__c",
  "objectives.funnelBPercentage": "Funnel_B_Percentage__c",
  "objectives.funnelCPercentage": "Funnel_C_Percentage__c",
  "objectives.funnelDPercentage": "Funnel_D_Percentage__c",
  "objectives.funnelEPercentage": "Funnel_E_Percentage__c",
  "objectives.totalApplied": "Total_Applied__c",
  "objectives.totalQualified": "Total_Qualified__c",
  "objectives.invitedAvailableContributors":
    "Invited_Available_Contributors__c",
  "objectives.appReceived": "App_Received__c",
  "objectives.matchedContributors": "Matched_Contributors__c",
  "objectives.removed": "Removed__c",
  "objectives.qualifiedContributors": "Qualified_Contributors__c",
  "objectives.activeContributors": "Active_Contributors__c",
  "objectives.productionContributors": "Production_Contributors__c",

  // Rates & Payment fields
  "objectives.nonUsFairpayRate": "Non_US_Fairpay_Rate__c",
  "objectives.nonUsProjectPayRate": "Non_US_Project_Pay_Rate__c",
  "objectives.projectRate": "Project_Rate__c",
  "objectives.projectObjectivePayRate": "Project_Objective_Pay_Rate__c",
  "objectives.projectPayRate": "Project_Pay_Rate__c",
  "objectives.clientProjectTypePayRate": "Client_Project_Type_Pay_Rate__c",
  "objectives.clientPayRate": "Client_Pay_Rate__c",
  "objectives.projectTypePayRate": "Project_Type_Pay_Rate__c",
  "objectives.payRateAboveFairpay": "Pay_Rate_Above_Fairpay__c",
  "objectives.flatPayRate": "Flat_Pay_Rate__c",
  "objectives.projectIncentiveRate": "Project_Incentive_Rate__c",
  "objectives.minimumRate": "Minimum_Rate__c",
  "objectives.maximumRate": "Maximum_Rate__c",
  "objectives.payInterval": "Pay_Interval__c",
  "objectives.unitPayRate": "Unit_Pay_Rate__c",

  // AC IDs & Access Control fields
  "objectives.acProjectId": "AC_Project_ID__c",
  "objectives.eventLogsAcProjectId": "Event_Logs_AC_Project_ID__c",
  "objectives.rosterUserGroupIdExclusive": "Roster_User_Group_ID_Exclusive__c",
  "objectives.rosterUserGroupId": "Roster_User_Group_ID__c",
  "objectives.rosterUserGroupName": "Roster_User_Group_Name__c",

  // Action Rules & Flagging fields
  "objectives.daysBetweenReminderEmails": "Days_Between_Reminder_Emails__c",
  "objectives.emailContentComplete": "Email_Content_Complete__c",
  "objectives.communicationTemplate": "Communication_Template__c",
  "objectives.contributorProjectDescriptionEmail":
    "Contributor_Project_Description_Email__c",
  "objectives.registrationStepsEmail": "Registration_Steps_Email__c",
  "objectives.qualificationStepsEmail": "Qualification_Steps_Email__c",
  "objectives.onboardingStepsEmail": "Onboarding_Steps_Email__c",
  "objectives.moderateToNoticeAvail": "Moderate_to_Notice_Avail__c",
  "objectives.rollingWeeksToModerateNoticeAvail":
    "Rolling_Weeks_to_Moderate_Notice_Avail__c",
  "objectives.severeToNoticeAvail": "Severe_to_Notice_Avail__c",
  "objectives.rollingWeeksToSevereNoticeAvail":
    "Rolling_Weeks_to_Severe_Notice_Avail__c",
  "objectives.noticeToRemovalAvail": "Notice_to_Removal_Avail__c",
  "objectives.rollingWeeksToRemovalAvail": "Rolling_Weeks_to_Removal_Avail__c",
  "objectives.moderateToNoticeProd": "Moderate_to_Notice_Prod__c",
  "objectives.rollingWeeksToModerateNoticeProd":
    "Rolling_Weeks_to_Moderate_Notice_Prod__c",
  "objectives.severeToNoticeProd": "Severe_to_Notice_Prod__c",
  "objectives.rollingWeeksToSevereNoticeProd":
    "Rolling_Weeks_to_Severe_Notice_Prod__c",
  "objectives.noticeToRemovalProd": "Notice_to_Removal_Prod__c",
  "objectives.rollingWeeksToRemovalProd": "Rolling_Weeks_to_Removal_Prod__c",

  // Admin fields
  "objectives.overrideDnh": "Override_DNH__c",
  "objectives.displayLeverPostErrorMessage":
    "Display_Lever_Post_Error_Message__c",
  "objectives.createInvites": "Create_Invites__c",
  "objectives.status": "Status__c",
  "objectives.countryLocale": "Country_Locale__c",
  "objectives.languageLocale": "Language_Locale__c",
  "objectives.dialectLocale": "Dialect_Locale__c",
  "objectives.refresh": "Refresh__c",
  "objectives.mercuryProjectObjectiveType": "Mercury_Project_Objective_Type__c",

  // System Information fields
  "objectives.createdBy": "Created_By__c",
  "objectives.lastModifiedBy": "Last_Modified_By__c",

  // Requirements (boolean flags stored on Project__c)
  "requirements.paymentSetupRequired": "Payment_Setup_Required__c",
  "requirements.clientToolAccountRequired": "Client_Tool_Account_Required__c",
  "requirements.manualActivationRequired": "Manual_Activation_Required__c",

  // Stats (counters on Project__c)
  "stats.totalApplied": "Total_Applied__c",
  "stats.totalQualified": "Total_Qualified__c",
  "stats.invitedAvailableContributors": "Invited_Available_Contributors__c",
  "stats.appReceived": "App_Received__c",
  "stats.matchedContributors": "Matched_Contributors__c",
  "stats.acAccount": "AC_Account__c",
  "stats.appliedContributors": "Applied_Contributors__c",
  "stats.registeredContributors": "Registered_Contributors__c",
  "stats.qualifiedContributors": "Qualified_Contributors__c",
  "stats.activeContributors": "Active_Contributors__c",
  "stats.productionContributors": "Production_Contributors__c",
  "stats.removed": "Removed__c",
};

// Reverse map for load
export const sfToUiFieldMap = Object.entries(uiToSfFieldMap).reduce(
  (acc, [ui, sf]) => {
    acc[sf] = ui;
    return acc;
  },
  {},
);

function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function setByPath(obj, path, value) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    if (cur[key] == null || typeof cur[key] !== "object") cur[key] = {};
    cur = cur[key];
  }
  cur[parts[parts.length - 1]] = value;
}

// Picklist normalizers to match SF values exactly
function normalizePaymentMethod(value) {
  const v = String(value || "").toLowerCase();
  if (v.includes("productivity only")) return "Productivity only";
  if (v.includes("self-reported and")) return "Self-reported and Productivity";
  if (v.includes("self-reported")) return "Self-reported only";
  return value || null;
}

function normalizeProjectType(value) {
  // Pass-through for now; UI values should match SF picklist where possible (e.g., "Search Evaluation", "Linguistics", "Translation", "Other")
  return value || null;
}

function normalizeStatus(value) {
  // Expected values: Draft | Open | Roster Hold | Closed
  return value || null;
}

// Convert UI projectData into a Salesforce Project__c record
export function toSalesforceProject(uiProject) {
  const record = {};
  if (!uiProject || typeof uiProject !== "object") return record;

  // Map fields
  for (const [uiPath, sfField] of Object.entries(uiToSfFieldMap)) {
    const raw = getByPath(uiProject, uiPath);
    if (raw === undefined) continue;
    let value = raw;
    if (sfField === "Project_Payment_Method__c")
      value = normalizePaymentMethod(raw);
    if (sfField === "Project_Type__c") value = normalizeProjectType(raw);
    if (sfField === "Project_Status__c") value = normalizeStatus(raw);
    record[sfField] = value;
  }

  // Preserve Id if present at top-level
  if (uiProject.id) record.Id = uiProject.id;
  if (uiProject.Id) record.Id = uiProject.Id;

  return record;
}

// Convert Salesforce Project__c record into UI projectData shape
export function fromSalesforceProject(sfRecordRaw) {
  const projectData = {
    info: {
      name: "",
      shortDescription: "",
      longDescription: "",
      shortName: "",
      appenPartner: "",
      account: "",
      programName: "",
      hireStartDate: "",
      predictedCloseDate: "",
      projectStatus: "",
      projectType: "",
      projectPriority: "",
      projectPaymentMethod: "",
      category: "",
      projectIdForReports: "",
      workdayProjectId: "",
      accountName: "",
      // projectManagerId: "",
      // projectManagerName: "",
    },
    objectives: {
      // Information Section
      contributorFacingName: "",
      objectiveName: "",
      appendToObjectiveName: "",
      project: "",
      dateStart: "",
      dateEnd: "",
      selectionCriteria: "",
      projectObjectiveDescription: "",
      totalTargetProductivityHours: "",
      weeklyTargetProductionHours: "",
      weeklyContributorProductionHours: "",
      targetContributors: "",
      projectObjectiveIdForReports: "",

      // Requirements Section
      country: "",
      language: "",
      workType: "",
      dialect: "",
      degreeRequirement: "",
      languageSkillLevel: "",
      additionalManualMatchRequired: false,
      fluencyType: "",
      includeInCountryList: false,

      // Job Posting Section
      jobPostAction: "",
      jobPostStatus: "",
      leverJobPostingDistribution: "",
      jobDescriptionTemplate: "",
      jobPostingTitle: "",
      jobDescription: "",
      leverTeam: "",
      leverRequisitionId: "",
      locationType: "",
      leverJobPostingDate: "",
      leverDepartment: "",
      leverJobPostingId: "",
      leverLocation: "",
      leverWorkType: "",

      // Productivity & Quality Section
      productivityTargetType: "",
      targetProductionHours: "",
      mainQualityMetric: "",
      contributorQualityFlag: "",
      contributorProductivityFlag: "",
      contributorOverproductivityFlag: "",

      // Funnel Configuration
      funnelAPercentage: 100,
      funnelBPercentage: 0,
      funnelCPercentage: 0,
      funnelDPercentage: 0,
      funnelEPercentage: 0,
      totalApplied: "",
      totalQualified: "",
      invitedAvailableContributors: "",
      appReceived: "",
      matchedContributors: "",
      removed: "",
      qualifiedContributors: "",
      activeContributors: "",
      productionContributors: "",

      // Rates & Payment Section
      nonUsFairpayRate: "",
      nonUsProjectPayRate: "",
      projectRate: "",
      projectObjectivePayRate: "",
      projectPayRate: "",
      clientProjectTypePayRate: "",
      clientPayRate: "",
      projectTypePayRate: "",
      payRateAboveFairpay: "",
      flatPayRate: "",
      projectIncentiveRate: "",
      minimumRate: "",
      maximumRate: "",
      payInterval: "/ hour",
      unitPayRate: "",

      // AC IDs & Access Control
      acProjectId: "",
      eventLogsAcProjectId: "",
      rosterUserGroupIdExclusive: "",
      rosterUserGroupId: "",
      rosterUserGroupName: "",

      // Action Rules & Flagging
      daysBetweenReminderEmails: 5,
      emailContentComplete: false,
      communicationTemplate: "",
      contributorProjectDescriptionEmail: "",
      registrationStepsEmail: "",
      qualificationStepsEmail: "",
      onboardingStepsEmail: "",
      moderateToNoticeAvail: "",
      rollingWeeksToModerateNoticeAvail: 6,
      severeToNoticeAvail: "",
      rollingWeeksToSevereNoticeAvail: 6,
      noticeToRemovalAvail: "",
      rollingWeeksToRemovalAvail: 6,
      moderateToNoticeProd: "",
      rollingWeeksToModerateNoticeProd: 6,
      severeToNoticeProd: "",
      rollingWeeksToSevereNoticeProd: 6,
      noticeToRemovalProd: "",
      rollingWeeksToRemovalProd: 6,

      // Admin Section
      overrideDnh: false,
      displayLeverPostErrorMessage: false,
      createInvites: false,
      status: "Draft",
      countryLocale: "",
      languageLocale: "",
      dialectLocale: "",
      refresh: false,
      mercuryProjectObjectiveType: "",

      // System Information
      createdBy: "",
      lastModifiedBy: "",
    },
    requirements: {
      paymentSetupRequired: true,
      clientToolAccountRequired: false,
      manualActivationRequired: false,
    },
    stats: {
      totalApplied: 0,
      totalQualified: 0,
      invitedAvailableContributors: 0,
      appReceived: 0,
      matchedContributors: 0,
      acAccount: 0,
      appliedContributors: 0,
      registeredContributors: 0,
      qualifiedContributors: 0,
      activeContributors: 0,
      productionContributors: 0,
      removed: 0,
    },
    qualifications: [],
    qualificationSteps: [],
    pages: [],
    images: [],
    settings: {
      autoApprove: false,
      requireApproval: true,
      maxWorkers: 100,
    },
  };

  if (
    !sfRecordRaw ||
    typeof sfRecordRaw !== "object" ||
    !sfRecordRaw?.data?.[0]
  )
    return { id: null, ...projectData };

  const sfRecord = sfRecordRaw.data[0];

  for (const [sfField, uiPath] of Object.entries(sfToUiFieldMap)) {
    const raw = sfRecord[sfField];
    if (raw === undefined) continue;
    setByPath(projectData, uiPath, raw);
  }

  // Additional read-only mappings for display
  if (sfRecord.Job_Category__c !== undefined) {
    projectData.info.category = sfRecord.Job_Category__c;
  }
  if (sfRecord.Project_ID_for_Reports__c !== undefined) {
    projectData.info.projectIdForReports = sfRecord.Project_ID_for_Reports__c;
  }
  if (sfRecord.Workday_Project_ID__c !== undefined) {
    projectData.info.workdayProjectId = sfRecord.Workday_Project_ID__c;
  }
  if (sfRecord.Contributor_Project_Name__c !== undefined) {
    projectData.objectives.contributorFacingName =
      sfRecord.Contributor_Project_Name__c;
    // Also map to metadata for consistency with UI
    projectData.info.contributorProjectName =
      sfRecord.Contributor_Project_Name__c;
  }

  // Relationship display names (populated when API returns relationship fields)
  if (sfRecord.Account__r?.Name) {
    projectData.info.accountName = sfRecord.Account__r.Name;
  }
  if (sfRecord.Project_Manager__r?.Name) {
    projectData.info.projectManagerName = sfRecord.Project_Manager__r.Name;
  }

  // Active Requirement flags
  if (sfRecord.Payment_Setup_Required__c !== undefined) {
    projectData.requirements.paymentSetupRequired = Boolean(
      sfRecord.Payment_Setup_Required__c,
    );
  }
  if (sfRecord.Client_Tool_Account_Required__c !== undefined) {
    projectData.requirements.clientToolAccountRequired = Boolean(
      sfRecord.Client_Tool_Account_Required__c,
    );
  }
  if (sfRecord.Manual_Activation_Required__c !== undefined) {
    projectData.requirements.manualActivationRequired = Boolean(
      sfRecord.Manual_Activation_Required__c,
    );
  }

  // Stats counters
  const statsMap = {
    Total_Applied__c: "totalApplied",
    Total_Qualified__c: "totalQualified",
    Invited_Available_Contributors__c: "invitedAvailableContributors",
    App_Received__c: "appReceived",
    Matched_Contributors__c: "matchedContributors",
    AC_Account__c: "acAccount",
    Applied_Contributors__c: "appliedContributors",
    Registered_Contributors__c: "registeredContributors",
    Qualified_Contributors__c: "qualifiedContributors",
    Active_Contributors__c: "activeContributors",
    Production_Contributors__c: "productionContributors",
    Removed__c: "removed",
  };
  for (const [sfField, uiKey] of Object.entries(statsMap)) {
    const value = sfRecord[sfField];
    if (value !== undefined && value !== null) {
      projectData.stats[uiKey] = Number(value);
    }
  }

  const id = sfRecord.Id || null;
  console.log("sfRecord", sfRecord);
  console.log("projectData", projectData);
  return { id, ...projectData };
}
